import React, { useState, useEffect, useContext, createContext } from 'react';
import { Table, Product, Category, Order, OrderItem, PaymentMethod } from '../types';
import * as storageService from '../services/storageService';
import * as authService from '../services/authService';
import * as printService from '../services/printService';
import { User } from '../services/authService';
import { useToast } from '@/hooks/use-toast';
import { syncOrderWithErp, generateGuid, isOrderModifiedSinceLastSync, calculateTableTotal } from '../services/orderApiService';

interface AppContextType {
  tables: Table[];
  products: Product[];
  categories: Category[];
  orders: Order[];
  paymentMethods: PaymentMethod[];
  activeTable: Table | null;
  currentUser: Omit<User, 'password'> | null;
  setCurrentUser: (user: User | null) => void;
  setActiveTable: (table: Table | null) => void;
  createNewOrder: (tableId: string, type: Order['type']) => Order;
  addItemToOrder: (orderId: string, product: Product, quantity: number, notes?: string) => void;
  updateItemQuantity: (orderId: string, itemId: string, quantity: number) => void;
  removeItemFromOrder: (orderId: string, itemId: string) => void;
  cancelOrderItem: (orderId: string, itemId: string, reason?: string) => void;
  updateOrderItemStatus: (orderId: string, itemId: string, status: OrderItem['status']) => void;
  completeOrder: (orderId: string, paymentMethodId?: string) => void;
  cancelTable: (tableId: string, reason: string) => void;
  printOrder: (orderId: string) => Promise<boolean>;
  kitchenOrders: { order: Order, items: OrderItem[] }[];
  deliveryOrders: Order[];
  refreshData: () => void;
  createNewTable: (number: number, seats: number) => Table;
  updateTableSeats: (tableId: string, seats: number) => Promise<void>;
  updateTableObservations: (tableId: string, observations: string) => Promise<void>;
  logout: () => void;
  synchronizeOrder: (orderId: string, autoSync?: boolean) => Promise<boolean>;
  getSyncStatusLabel: (status?: 'pending' | 'synced' | 'failed') => string;
  getSyncStatusClass: (status?: 'pending' | 'synced' | 'failed') => string;
  lastPageVisit: string | null;
  applyOrderDiscount: (orderId: string, amount: number, type: 'percentage' | 'fixed') => void;
  addOrder: (order: Order) => void;
  finalizeTable: (tableId: string, paymentMethods: PaymentMethod[], paymentValues: number[]) => Promise<boolean>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tables, setTables] = useState<Table[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [activeTable, setActiveTable] = useState<Table | null>(null);
  const [kitchenOrders, setKitchenOrders] = useState<{ order: Order, items: OrderItem[] }[]>([]);
  const [deliveryOrders, setDeliveryOrders] = useState<Order[]>([]);
  const [currentUser, setCurrentUser] = useState<Omit<User, 'password'> | null>(null);
  const [lastPageVisit, setLastPageVisit] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    console.log("Inicializando AppProvider");
    storageService.initializeStorage();
    authService.initializeAuthStorage();
    refreshData();
    
    const user = authService.getCurrentUser();
    if (user) {
      console.log("Usuário autenticado encontrado:", user);
      setCurrentUser(user);
    } else {
      console.log("Nenhum usuário autenticado encontrado");
    }
    
    const handleRouteChange = () => {
      const currentPath = window.location.pathname;
      
      if (lastPageVisit && lastPageVisit !== currentPath) {
        console.log(`Detectada mudança de rota: ${lastPageVisit} -> ${currentPath}`);
      }
      
      setLastPageVisit(currentPath);
    };
    
    handleRouteChange();
    
    window.addEventListener('popstate', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [lastPageVisit]);

  const syncPendingOrders = async (silent: boolean = false) => {
    try {
      const pendingOrders = orders.filter(order => 
        (order.status === 'paid' && 
         (order.syncStatus === 'pending' || 
          (order.syncStatus === 'failed' && order.lastSyncDate && 
           new Date().getTime() - order.lastSyncDate.getTime() > 5 * 60 * 1000)))
      );
      
      if (pendingOrders.length === 0) {
        console.log('Nenhum pedido pendente para sincronização');
        return;
      }
      
      if (!silent) {
        toast({
          title: 'Sincronizando pedidos',
          description: `Sincronizando ${pendingOrders.length} pedidos pendentes...`,
        });
      }
      
      let successCount = 0;
      
      for (const order of pendingOrders) {
        const paymentMethod = order.paymentMethodId 
          ? paymentMethods.find(p => p.id === order.paymentMethodId) 
          : undefined;
        
        const vendedorId = currentUser?.vendedor_id ? Number(currentUser.vendedor_id) : 1;
        
        const success = await synchronizeOrder(order.id, true);
        if (success) successCount++;
      }
      
      if (!silent && successCount > 0) {
        toast({
          title: 'Sincronização concluída',
          description: `${successCount} de ${pendingOrders.length} pedidos foram sincronizados com sucesso`,
        });
      }
    } catch (error) {
      console.error('Erro ao sincronizar pedidos pendentes:', error);
      if (!silent) {
        toast({
          title: 'Erro na sincronização',
          description: 'Ocorreu um erro ao sincronizar os pedidos pendentes',
          variant: 'destructive',
        });
      }
    }
  };

  const refreshData = async () => {
    const tablesData = await storageService.getTables();
    setTables(tablesData);
    
    const productsData = await storageService.getProducts();
    setProducts(productsData);
    
    const categoriesData = await storageService.getCategories();
    setCategories(categoriesData);
    
    const ordersData = await storageService.getOrders();
    setOrders(ordersData);
    
    const kitchenOrdersData = await storageService.getKitchenOrders();
    setKitchenOrders(kitchenOrdersData);
    
    const deliveryOrdersData = await storageService.getDeliveryOrders();
    setDeliveryOrders(deliveryOrdersData);
    
    const paymentMethodsData = await storageService.getPaymentMethods();
    setPaymentMethods(paymentMethodsData);
  };

  const logout = () => {
    authService.logout();
    setCurrentUser(null);
    toast({
      title: "Logout realizado",
      description: "Você foi desconectado do sistema",
    });
  };

  const createNewTable = (number: number, seats: number): Table => {
    try {
      const newTable = {
        id: storageService.generateId(),
        number,
        seats,
        status: 'available' as const,
        orders: [],
        createdAt: new Date()
      };
      
      storageService.storeTables([...tables, newTable]);
      refreshData();
      
      toast({
        title: "Mesa criada",
        description: `Nova mesa ${number} com ${seats} lugares foi criada`,
      });
      
      return newTable;
    } catch (error) {
      if (error instanceof Error) {
        toast({
          title: "Erro ao criar mesa",
          description: error.message,
          variant: "destructive"
        });
      }
      throw error;
    }
  };

  const updateTableSeats = async (tableId: string, seats: number): Promise<void> => {
    try {
      const table = await storageService.getTable(tableId);
      
      if (!table) {
        throw new Error("Mesa não encontrada");
      }
      
      table.seats = seats;
      await storageService.updateTable(table);
      await refreshData();
      
      if (activeTable && activeTable.id === tableId) {
        setActiveTable({...table});
      }
      
    } catch (error) {
      if (error instanceof Error) {
        toast({
          title: "Erro ao atualizar lugares",
          description: error.message,
          variant: "destructive"
        });
      }
      throw error;
    }
  };

  const createNewOrder = (tableId: string, type: Order['type']): Order => {
    const table = tables.find(t => t.id === tableId);
    
    const newOrder: Order = {
      id: storageService.generateId(),
      tableId,
      tableNumber: table?.number, // Adicionando o número da mesa diretamente no pedido
      items: [],
      status: 'open',
      type,
      createdAt: new Date(),
      updatedAt: new Date(),
      totalAmount: 0,
      externalId: generateGuid(),
      syncStatus: 'pending'
    };
    
    storageService.createOrder(newOrder);
    refreshData();
    
    (async () => {
      const updatedTable = await storageService.getTable(tableId);
      if (updatedTable) {
        setActiveTable(updatedTable);
      }
    })();
    
    toast({
      title: "Pedido criado",
      description: `Novo pedido criado para a mesa ${tables.find(t => t.id === tableId)?.number}`,
    });
    
    return newOrder;
  };

  const addItemToOrder = async (orderId: string, product: Product, quantity: number, notes?: string) => {
    try {
      const order = await storageService.getOrder(orderId);
      
      if (!order) return;
      
      const existingItem = order.items.find(item => item.product.id === product.id);
      
      if (existingItem) {
        existingItem.quantity += quantity;
        existingItem.notes = notes || existingItem.notes;
      } else {
        const newItem: OrderItem = {
          id: storageService.generateId(),
          productId: product.id,
          product,
          quantity,
          notes,
          status: 'pending'
        };
        order.items.push(newItem);
      }
      
      order.totalAmount = order.items.reduce(
        (total, item) => total + (item.product.price * item.quantity),
        0
      );
      
      order.updatedAt = new Date();
      await storageService.updateOrder(order);
      await refreshData();
      
      toast({
        title: "Item adicionado",
        description: `${quantity}x ${product.name} adicionado ao pedido`,
        duration: 3000,
      });
      
      const updatedTable = await storageService.getTable(order.tableId);
      if (updatedTable) {
        setActiveTable(updatedTable);
      }
    } catch (error) {
      console.error("Erro ao adicionar item:", error);
    }
  };

  const updateItemQuantity = async (orderId: string, itemId: string, quantity: number) => {
    try {
      const order = await storageService.getOrder(orderId);
      
      if (!order) return;
      
      const item = order.items.find(item => item.id === itemId);
      
      if (!item) return;
      
      if (quantity <= 0) {
        await removeItemFromOrder(orderId, itemId);
        return;
      }
      
      item.quantity = quantity;
      
      order.totalAmount = order.items.reduce(
        (total, item) => total + (item.product.price * item.quantity),
        0
      );
      
      order.updatedAt = new Date();
      await storageService.updateOrder(order);
      await refreshData();
      
      const updatedTable = await storageService.getTable(order.tableId);
      if (updatedTable) {
        setActiveTable(updatedTable);
      }
    } catch (error) {
      console.error("Erro ao atualizar quantidade:", error);
    }
  };

  const removeItemFromOrder = async (orderId: string, itemId: string) => {
    try {
      const order = await storageService.getOrder(orderId);
      
      if (!order) return;
      
      const itemIndex = order.items.findIndex(item => item.id === itemId);
      
      if (itemIndex === -1) return;
      
      const removedItem = order.items[itemIndex];
      
      order.items.splice(itemIndex, 1);
      
      order.totalAmount = order.items.reduce(
        (total, item) => total + (item.product.price * item.quantity),
        0
      );
      
      order.updatedAt = new Date();
      await storageService.updateOrder(order);
      await refreshData();
      
      toast({
        title: "Item removido",
        description: `${removedItem.product.name} removido do pedido`,
        duration: 3000,
      });
      
      const updatedTable = await storageService.getTable(order.tableId);
      if (updatedTable) {
        setActiveTable(updatedTable);
      }
    } catch (error) {
      console.error("Erro ao remover item:", error);
    }
  };

  const cancelOrderItem = async (orderId: string, itemId: string, reason?: string) => {
    try {
      const order = await storageService.getOrder(orderId);
      
      if (!order) return;
      
      const itemIndex = order.items.findIndex(item => item.id === itemId);
      
      if (itemIndex === -1) return;
      
      const item = order.items[itemIndex];
      
      item.status = 'cancelled';
      item.cancelReason = reason || 'Cancelado pelo usuário';
      
      order.totalAmount = order.items.reduce(
        (total, item) => total + (item.status !== 'cancelled' ? (item.product.price * item.quantity) : 0),
        0
      );
      
      order.updatedAt = new Date();
      await storageService.updateOrder(order);
      await refreshData();
      
      toast({
        title: "Item cancelado",
        description: `${item.product.name} foi cancelado do pedido`,
        duration: 3000,
      });
      
      const updatedTable = await storageService.getTable(order.tableId);
      if (updatedTable && activeTable && updatedTable.id === activeTable.id) {
        setActiveTable(updatedTable);
      }
    } catch (error) {
      console.error("Erro ao cancelar item:", error);
      toast({
        title: "Erro ao cancelar item",
        description: "Não foi possível cancelar o item do pedido",
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  const updateOrderItemStatus = async (orderId: string, itemId: string, status: OrderItem['status']) => {
    try {
      await storageService.updateOrderItemStatus(orderId, itemId, status);
      await refreshData();
      
      const order = await storageService.getOrder(orderId);
      if (order) {
        const item = order.items.find(item => item.id === itemId);
        if (item) {
          toast({
            title: "Status atualizado",
            description: `${item.product.name} agora está ${getStatusLabel(status)}`,
            duration: 3000,
          });
        }
      }
      
      if (activeTable) {
        const updatedTable = await storageService.getTable(activeTable.id);
        if (updatedTable) {
          setActiveTable(updatedTable);
        }
      }
    } catch (error) {
      console.error("Erro ao atualizar status:", error);
    }
  };

  const printOrder = async (orderId: string): Promise<boolean> => {
    try {
      const order = await storageService.getOrder(orderId);
      
      if (!order) {
        toast({
          title: "Erro na impressão",
          description: "Pedido não encontrado",
          variant: "destructive"
        });
        return false;
      }
      
      let tableName;
      if (order.tableId) {
        const table = await storageService.getTable(order.tableId);
        if (table) {
          tableName = `Mesa ${table.number}`;
        }
      }
      
      const receipt = printService.formatOrderForPrinting(order, tableName);
      
      await printService.printReceipt(receipt);
      
      toast({
        title: "Impressão enviada",
        description: "O recibo foi enviado para impressão",
      });
      
      return true;
    } catch (error) {
      let errorMessage = "Erro ao imprimir o recibo";
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Erro na impressão",
        description: errorMessage,
        variant: "destructive"
      });
      
      return false;
    }
  };

  const cancelTable = async (tableId: string, reason: string): Promise<void> => {
    try {
      const table = await storageService.getTable(tableId);
      
      if (!table) {
        throw new Error("Mesa não encontrada");
      }
      
      table.status = 'cancelled';
      table.cancelReason = reason;
      table.cancelDate = new Date();
      
      for (const order of table.orders) {
        if (order.status === 'open') {
          order.status = 'cancelled';
          order.cancelReason = reason;
          await storageService.updateOrder(order);
        }
      }
      
      await storageService.updateTable(table);
      await refreshData();
      
      toast({
        title: "Mesa cancelada",
        description: `Mesa ${table.number} foi cancelada: ${reason}`,
      });
      
      if (activeTable && activeTable.id === tableId) {
        setActiveTable({...table});
      }
      
    } catch (error) {
      if (error instanceof Error) {
        toast({
          title: "Erro ao cancelar mesa",
          description: error.message,
          variant: "destructive"
        });
      }
      throw error;
    }
  };

  const completeOrder = async (orderId: string, paymentMethodId?: string) => {
    try {
      const order = await storageService.getOrder(orderId);
      
      if (!order) return;
      
      order.status = 'paid';
      order.updatedAt = new Date();
      order.paymentDate = new Date();
      
      if (paymentMethodId) {
        order.paymentMethodId = paymentMethodId;
      }
      
      await storageService.updateOrder(order);
      
      await synchronizeOrder(orderId);
      
      await refreshData();
      
      const table = await storageService.getTable(order.tableId);
      
      toast({
        title: "Pedido finalizado",
        description: `Pedido da mesa ${table ? table.number : ''} foi pago e finalizado`,
        duration: 3000,
      });
      
      if (table && activeTable && table.id === activeTable.id) {
        const updatedTable = await storageService.getTable(table.id);
        if (updatedTable) {
          setActiveTable(updatedTable);
        }
      }
    } catch (error) {
      console.error("Erro ao completar pedido:", error);
    }
  };

  const synchronizeOrder = async (orderId: string, autoSync: boolean = false): Promise<boolean> => {
    try {
      const order = await storageService.getOrder(orderId);
      
      if (!order) {
        toast({
          title: "Erro de sincronização",
          description: "Pedido não encontrado",
          variant: "destructive"
        });
        return false;
      }
      
      if (order.items.length === 0) {
        toast({
          title: "Erro de sincronização",
          description: "Não há itens no pedido para sincronizar",
          variant: "destructive"
        });
        return false;
      }
      
      let paymentMethods: PaymentMethod[] = [];
      if (order.paymentMethodId) {
        const paymentMethod = paymentMethods.find(p => p.id === order.paymentMethodId);
        if (paymentMethod) {
          paymentMethods = [paymentMethod];
        }
      }
      
      const vendedorId = currentUser?.vendedor_id ? Number(currentUser.vendedor_id) : 1;
      
      const paymentValues = paymentMethods.length > 0 ? [order.totalAmount] : [];
      
      const erpId = await syncOrderWithErp(order, paymentMethods, paymentValues, vendedorId);
      
      if (erpId > 0) {
        order.erpId = erpId;
        order.syncStatus = 'synced';
        order.lastSyncDate = new Date();
        order.syncError = undefined;
        
        await storageService.updateOrder(order);
        await refreshData();
        
        if (activeTable && activeTable.id === order.tableId) {
          const updatedTable = await storageService.getTable(order.tableId);
          if (updatedTable) {
            setActiveTable(updatedTable);
          }
        }
        
        if (!autoSync) {
          toast({
            title: "Sincronização concluída",
            description: `Pedido sincronizado com sucesso. ID ERP: ${erpId}`,
          });
        }
        
        return true;
      } else {
        order.syncStatus = 'failed';
        order.lastSyncDate = new Date();
        order.syncError = 'Falha ao sincronizar com o ERP';
        
        await storageService.updateOrder(order);
        await refreshData();
        
        if (!autoSync) {
          toast({
            title: "Falha na sincronização",
            description: "Não foi possível sincronizar o pedido com o ERP",
            variant: "destructive"
          });
        }
        
        return false;
      }
    } catch (error) {
      console.error("Erro ao sincronizar pedido:", error);
      
      if (!autoSync) {
        toast({
          title: "Erro de sincronização",
          description: error instanceof Error ? error.message : "Erro desconhecido",
          variant: "destructive"
        });
      }
      
      return false;
    }
  };

  const getSyncStatusLabel = (status?: 'pending' | 'synced' | 'failed'): string => {
    switch (status) {
      case 'pending': return 'Pendente de sincronização';
      case 'synced': return 'Sincronizado';
      case 'failed': return 'Falha na sincronização';
      default: return 'Pendente de sincronização';
    }
  };

  const getSyncStatusClass = (status?: 'pending' | 'synced' | 'failed'): string => {
    switch (status) {
      case 'pending': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'synced': return 'bg-green-100 text-green-800 border-green-200';
      case 'failed': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-orange-100 text-orange-800 border-orange-200';
    }
  };

  const updateTableObservations = async (tableId: string, observations: string): Promise<void> => {
    try {
      await storageService.updateTableObservations(tableId, observations);
      
      toast({
        title: "Observações atualizadas",
        description: "As observações da mesa foram atualizadas com sucesso",
      });
      
      await refreshData();
      
      if (activeTable && activeTable.id === tableId) {
        const updatedTable = await storageService.getTable(tableId);
        if (updatedTable) {
          setActiveTable(updatedTable);
        }
      }
      
    } catch (error) {
      if (error instanceof Error) {
        toast({
          title: "Erro ao atualizar observações",
          description: error.message,
          variant: "destructive"
        });
      }
      throw error;
    }
  };

  const getStatusLabel = (status: OrderItem['status']): string => {
    switch (status) {
      case 'pending': return 'pendente';
      case 'preparing': return 'em preparo';
      case 'ready': return 'pronto';
      case 'delivered': return 'entregue';
      case 'cancelled': return 'cancelado';
      default: return status;
    }
  };

  const applyOrderDiscount = async (orderId: string, amount: number, type: 'percentage' | 'fixed') => {
    try {
      const order = await storageService.getOrder(orderId);
      
      if (!order) return;
      
      order.discount = amount;
      order.discountType = type;
      
      const subtotal = order.items.reduce(
        (total, item) => total + (item.status !== 'cancelled' ? (item.product.price * item.quantity) : 0),
        0
      );
      
      if (type === 'percentage') {
        const discountValue = (subtotal * amount) / 100;
        order.totalAmount = subtotal - discountValue;
      } else {
        order.totalAmount = subtotal - Math.min(amount, subtotal);
      }
      
      order.updatedAt = new Date();
      await storageService.updateOrder(order);
      await refreshData();
      
      const updatedTable = await storageService.getTable(order.tableId);
      if (updatedTable && activeTable && updatedTable.id === activeTable.id) {
        setActiveTable(updatedTable);
      }
    } catch (error) {
      console.error("Erro ao aplicar desconto:", error);
      toast({
        title: "Erro ao aplicar desconto",
        description: "Não foi possível aplicar o desconto ao pedido",
        variant: "destructive",
      });
    }
  };

  const addOrder = (order: Order) => {
    setOrders([...orders, order]);
    storageService.storeOrders([...orders, order]);
  };

  const finalizeTable = async (tableId: string, paymentMethods: PaymentMethod[], paymentValues: number[]): Promise<boolean> => {
    try {
      const table = await storageService.getTable(tableId);
      
      if (!table) {
        toast({
          title: "Erro ao finalizar mesa",
          description: "Mesa não encontrada",
          variant: "destructive"
        });
        return false;
      }
      
      const openOrders = table.orders.filter(o => o.status === 'open');
      
      if (openOrders.length === 0) {
        toast({
          title: "Erro ao finalizar mesa",
          description: "Não há pedidos abertos nesta mesa",
          variant: "destructive"
        });
        return false;
      }
      
      const tableGuid = generateGuid();
      let totalAmount = 0;
      
      for (const order of openOrders) {
        totalAmount += order.totalAmount;
      }
      
      for (const order of openOrders) {
        order.status = 'paid';
        order.updatedAt = new Date();
        order.paymentDate = new Date();
        
        if (paymentMethods.length > 0) {
          order.paymentMethodId = paymentMethods[0].id;
        }
        
        await storageService.updateOrder(order);
        
        const vendedorId = currentUser?.vendedor_id ? Number(currentUser.vendedor_id) : 1;
        await syncOrderWithErp(order, paymentMethods, paymentValues, vendedorId, 0, false, tableGuid);
      }
      
      table.status = 'available';
      await storageService.updateTable(table);
      
      await refreshData();
      
      toast({
        title: "Mesa finalizada",
        description: `Mesa ${table.number} foi finalizada com sucesso`,
      });
      
      return true;
    } catch (error) {
      console.error("Erro ao finalizar mesa:", error);
      
      toast({
        title: "Erro ao finalizar mesa",
        description: error instanceof Error ? error.message : "Ocorreu um erro desconhecido",
        variant: "destructive"
      });
      
      return false;
    }
  };

  return (
    <AppContext.Provider value={{
      tables,
      products,
      categories,
      orders,
      paymentMethods,
      activeTable,
      currentUser,
      setCurrentUser,
      setActiveTable,
      createNewOrder,
      addItemToOrder,
      updateItemQuantity,
      removeItemFromOrder,
      cancelOrderItem,
      updateOrderItemStatus,
      completeOrder,
      cancelTable,
      printOrder,
      kitchenOrders,
      deliveryOrders,
      refreshData,
      createNewTable,
      updateTableSeats,
      updateTableObservations,
      logout,
      synchronizeOrder,
      getSyncStatusLabel,
      getSyncStatusClass,
      lastPageVisit,
      applyOrderDiscount,
      addOrder,
      finalizeTable
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
